import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import ClassCalendar from "../components/ClassCalendar";
import { formatLatexForDisplay } from "../lib/latexUtils";

function getInitials(name) {
  if (!name) return "T";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function getTestTimingStatus(set) {
  if (!set || (set.assignmentType !== "test" && set.sourceType !== "exam_paper")) return null;
  const now = new Date();
  const startTime = set.startTime ? new Date(set.startTime) : null;
  const endTime = set.dueTimestamp || set.dueDate ? new Date(set.dueTimestamp || set.dueDate) : null;

  if (set.isGraded) {
    return { status: "graded", label: "✓ Graded", color: "#059669", bg: "#ecfdf5", border: "#a7f3d0", canStart: false };
  }
  if (set.isLocked) {
    return { status: "submitted", label: "Submitted", color: "#4338ca", bg: "#ede9fe", border: "#ddd6fe", canStart: false };
  }
  if (startTime && now < startTime) {
    const diffSec = Math.max(0, Math.floor((startTime.getTime() - now.getTime()) / 1000));
    const hrs = Math.floor(diffSec / 3600);
    const mins = Math.floor((diffSec % 3600) / 60);
    const countdown = hrs > 0 ? `${hrs}h ${mins}m` : `${Math.max(1, mins)}m`;
    return {
      status: "upcoming",
      label: `Starts in ${countdown}`,
      color: "#b45309",
      bg: "#fffbeb",
      border: "#fde68a",
      canStart: false,
      startTimeFormatted: startTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    };
  }
  if (endTime && now > endTime) {
    return { status: "expired", label: "Deadline Expired", color: "#dc2626", bg: "#fef2f2", border: "#fecaca", canStart: false };
  }
  if (endTime) {
    const diffSec = Math.max(0, Math.floor((endTime.getTime() - now.getTime()) / 1000));
    const hrs = Math.floor(diffSec / 3600);
    const mins = Math.floor((diffSec % 3600) / 60);
    const countdown = hrs > 0 ? `${hrs}h ${mins}m` : `${Math.max(1, mins)}m`;
    return {
      status: "active",
      label: `Active Now • ${countdown} left`,
      color: "#15803d",
      bg: "#f0fdf4",
      border: "#86efac",
      canStart: true
    };
  }
  return { status: "active", label: "Active Test", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", canStart: true };
}

export default function MyTutor({ apiBase, token, onPageBusyChange }) {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("coursework"); // "coursework" | "calendar"
  const [courseworkTypeFilter, setCourseworkTypeFilter] = useState("all"); // "all" | "test" | "homework"
  const [courseworkStatusFilter, setCourseworkStatusFilter] = useState("all"); // "all" | "pending" | "completed" | "graded"
  const [selectedTutorFilter, setSelectedTutorFilter] = useState("all");

  const [tutors, setTutors] = useState([]);
  const [assignedSets, setAssignedSets] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [expandedSetIds, setExpandedSetIds] = useState(new Set());
  const [selectedSessionModal, setSelectedSessionModal] = useState(null);
  const [selectedFeedbackModal, setSelectedFeedbackModal] = useState(null);

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");

    try {
      const [tutorsRes, setsRes, sessionsRes] = await Promise.all([
        fetch(`${apiBase}/api/student/my-tutors`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${apiBase}/api/questions/assigned-to-me`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${apiBase}/api/student/sessions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (tutorsRes.ok) {
        const tData = await tutorsRes.json();
        setTutors(Array.isArray(tData.tutors) ? tData.tutors : []);
      }

      if (setsRes.ok) {
        const sData = await setsRes.json();
        setAssignedSets(Array.isArray(sData.sets) ? sData.sets : []);
      }

      if (sessionsRes.ok) {
        const sessData = await sessionsRes.json();
        setSessions(Array.isArray(sessData.sessions) ? sessData.sessions : []);
      }
    } catch (err) {
      setError(err.message || "Failed to load tutor data.");
    } finally {
      setLoading(false);
    }
  }, [apiBase, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleSetExpand = (id) => {
    setExpandedSetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Filtered Coursework
  const filteredSets = useMemo(() => {
    return assignedSets.filter((set) => {
      const isTest = set.assignmentType === "test" || set.sourceType === "exam_paper";
      if (courseworkTypeFilter === "test" && !isTest) return false;
      if (courseworkTypeFilter === "homework" && isTest) return false;

      if (selectedTutorFilter !== "all" && set.createdBy !== selectedTutorFilter) return false;

      if (courseworkStatusFilter === "pending") {
        if (isTest && (set.isLocked || set.isGraded)) return false;
        if (!isTest && set.completedCount >= set.questionCount) return false;
      } else if (courseworkStatusFilter === "completed") {
        if (isTest && !set.isLocked && !set.isGraded) return false;
        if (!isTest && set.completedCount < set.questionCount) return false;
      } else if (courseworkStatusFilter === "graded") {
        if (!set.isGraded) return false;
      }

      return true;
    });
  }, [assignedSets, courseworkTypeFilter, courseworkStatusFilter, selectedTutorFilter]);

  // Filtered Sessions for Calendar
  const filteredSessions = useMemo(() => {
    if (selectedTutorFilter === "all") return sessions;
    return sessions.filter((s) => s.tutorUid === selectedTutorFilter);
  }, [sessions, selectedTutorFilter]);

  // Today and upcoming session counts for contextual tab badge
  const { todaySessionsCount, upcomingSessionsCount } = useMemo(() => {
    const today = new Date();
    const now = today.getTime();
    let todayCount = 0;
    let upcomingCount = 0;

    filteredSessions.forEach((s) => {
      if (s.status === "cancelled" || !s.startTime) return;
      const d = new Date(s.startTime);
      const isToday =
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate();
      if (isToday) todayCount++;
      if (new Date(s.endTime || s.startTime).getTime() >= now) upcomingCount++;
    });

    return { todaySessionsCount: todayCount, upcomingSessionsCount: upcomingCount };
  }, [filteredSessions]);

  // Pending items count for badge
  const pendingCount = useMemo(() => {
    return assignedSets.filter((s) => {
      const isTest = s.assignmentType === "test" || s.sourceType === "exam_paper";
      if (isTest) return !s.isLocked && !s.isGraded;
      return s.completedCount < s.questionCount;
    }).length;
  }, [assignedSets]);

  return (
    <div className="my-tutor-page" style={{ padding: "28px 24px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* ── Page Header ── */}
      <div style={{ marginBottom: "24px" }}>
        <h1 style={{ fontSize: "24px", fontWeight: "700", color: "#0f172a", margin: "0 0 6px 0" }}>
          My Tutor
        </h1>
        <p style={{ fontSize: "14px", color: "var(--muted)", margin: 0 }}>
          Manage your tutor-guided tests, homework assignments, and upcoming live classes.
        </p>
      </div>

      {error && (
        <div style={{ padding: "12px 16px", backgroundColor: "#fee2e2", color: "#991b1b", borderRadius: "8px", marginBottom: "20px", fontSize: "13px" }}>
          {error}
        </div>
      )}

      {/* ── Active Tutors Banner (Elegant Profile Cards) ── */}
      {tutors.length > 0 && (
        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "24px" }}>
          {tutors.map((tutor) => (
            <div
              key={tutor.uid || tutor.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                backgroundColor: "#ffffff",
                border: "1px solid var(--border)",
                borderRadius: "12px",
                padding: "12px 18px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.02)"
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: "#eff6ff",
                  color: "#1d4ed8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "700",
                  flexShrink: 0,
                  border: "1px solid #bfdbfe"
                }}
              >
                {getInitials(tutor.name)}
              </div>
              <div>
                <div style={{ fontWeight: "700", fontSize: "14px", color: "#0f172a" }}>
                  {tutor.name}
                </div>
                <div style={{ fontSize: "12px", color: "var(--muted)" }}>
                  {tutor.email}
                </div>
              </div>
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  backgroundColor: "#ecfdf5",
                  color: "#059669",
                  padding: "2px 8px",
                  borderRadius: "6px",
                  marginLeft: "8px"
                }}
              >
                Active Tutor
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Main Navigation Tabs ── */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          borderBottom: "1px solid var(--border)",
          paddingBottom: "12px",
          marginBottom: "24px"
        }}
      >
        <button
          type="button"
          onClick={() => setActiveTab("coursework")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 18px",
            borderRadius: "8px",
            border: activeTab === "coursework" ? "1.5px solid #2563eb" : "1px solid transparent",
            backgroundColor: activeTab === "coursework" ? "#eff6ff" : "transparent",
            color: activeTab === "coursework" ? "#1d4ed8" : "#475569",
            fontWeight: activeTab === "coursework" ? "700" : "600",
            fontSize: "14px",
            cursor: "pointer"
          }}
        >
          <span>Assigned Coursework</span>
          {pendingCount > 0 && (
            <span
              style={{
                fontSize: "11px",
                fontWeight: "700",
                backgroundColor: "#ef4444",
                color: "#ffffff",
                padding: "1px 7px",
                borderRadius: "10px"
              }}
            >
              {pendingCount} due
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("calendar")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 18px",
            borderRadius: "8px",
            border: activeTab === "calendar" ? "1.5px solid #2563eb" : "1px solid transparent",
            backgroundColor: activeTab === "calendar" ? "#eff6ff" : "transparent",
            color: activeTab === "calendar" ? "#1d4ed8" : "#475569",
            fontWeight: activeTab === "calendar" ? "700" : "600",
            fontSize: "14px",
            cursor: "pointer"
          }}
        >
          <span>Live Sessions & Calendar</span>
          <span
            style={{
              fontSize: "11px",
              fontWeight: "600",
              backgroundColor: todaySessionsCount > 0 ? "#eff6ff" : "#f1f5f9",
              color: todaySessionsCount > 0 ? "#1d4ed8" : "#64748b",
              padding: "1px 8px",
              borderRadius: "10px"
            }}
          >
            {todaySessionsCount > 0
              ? `${todaySessionsCount} today`
              : upcomingSessionsCount > 0
              ? `${upcomingSessionsCount} upcoming`
              : "0"}
          </span>
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          TAB 1: ASSIGNED COURSEWORK (TESTS & HOMEWORK)
          ══════════════════════════════════════════════════════════════ */}
      {activeTab === "coursework" && (
        <div>
          {/* Sub-Filters Bar */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
              backgroundColor: "#ffffff",
              border: "1px solid var(--border)",
              borderRadius: "12px",
              padding: "12px 18px",
              marginBottom: "20px"
            }}
          >
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: "12px", fontWeight: "600", color: "var(--muted)", textTransform: "uppercase" }}>
                Type:
              </span>
              {[
                { id: "all", label: "All Coursework" },
                { id: "test", label: "Timed Tests" },
                { id: "homework", label: "Homework" }
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setCourseworkTypeFilter(t.id)}
                  style={{
                    padding: "4px 12px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: courseworkTypeFilter === t.id ? "700" : "500",
                    border: courseworkTypeFilter === t.id ? "1.5px solid #2563eb" : "1px solid #cbd5e1",
                    backgroundColor: courseworkTypeFilter === t.id ? "#eff6ff" : "#ffffff",
                    color: courseworkTypeFilter === t.id ? "#1d4ed8" : "#475569",
                    cursor: "pointer"
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: "12px", fontWeight: "600", color: "var(--muted)", textTransform: "uppercase" }}>
                Status:
              </span>
              {[
                { id: "all", label: "All" },
                { id: "pending", label: "Due / In Progress" },
                { id: "completed", label: "Completed" },
                { id: "graded", label: "Graded" }
              ].map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setCourseworkStatusFilter(s.id)}
                  style={{
                    padding: "4px 12px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: courseworkStatusFilter === s.id ? "700" : "500",
                    border: courseworkStatusFilter === s.id ? "1.5px solid #2563eb" : "1px solid #cbd5e1",
                    backgroundColor: courseworkStatusFilter === s.id ? "#eff6ff" : "#ffffff",
                    color: courseworkStatusFilter === s.id ? "#1d4ed8" : "#475569",
                    cursor: "pointer"
                  }}
                >
                  {s.label}
                </button>
              ))}

              {tutors.length > 1 && (
                <select
                  value={selectedTutorFilter}
                  onChange={(e) => setSelectedTutorFilter(e.target.value)}
                  style={{
                    padding: "5px 10px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    border: "1px solid var(--border)",
                    backgroundColor: "#ffffff",
                    color: "#0f172a"
                  }}
                >
                  <option value="all">All Tutors</option>
                  {tutors.map((tut) => (
                    <option key={tut.uid || tut.id} value={tut.uid || tut.id}>
                      {tut.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Coursework Cards List */}
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "var(--muted)" }}>
              Loading assigned coursework...
            </div>
          ) : filteredSets.length === 0 ? (
            <div
              style={{
                backgroundColor: "#ffffff",
                border: "1px dashed var(--border)",
                borderRadius: "14px",
                padding: "48px",
                textAlign: "center",
                color: "var(--muted)"
              }}
            >
              <div style={{ fontSize: "16px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                No coursework matches the selected filters.
              </div>
              <div style={{ fontSize: "13px" }}>
                Check back when your tutor assigns a new timed test or homework practice.
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {filteredSets.map((set) => {
                const isTest = set.assignmentType === "test" || set.sourceType === "exam_paper";
                const isExpanded = expandedSetIds.has(set.id);
                const tutor = tutors.find((t) => t.uid === set.createdBy);
                const testTiming = isTest ? getTestTimingStatus(set) : null;
                const feedback = set.tutorFeedback || set.questions?.find((q) => q.tutorFeedback?.notes)?.tutorFeedback || null;
                const hasFeedback = Boolean(feedback && (feedback.notes || (Array.isArray(feedback.focusPoints) && feedback.focusPoints.length > 0)));

                return (
                  <div
                    key={set.id}
                    style={{
                      backgroundColor: "#ffffff",
                      border: "1px solid var(--border)",
                      borderRadius: "14px",
                      padding: "20px 24px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                      transition: "border-color 0.15s ease"
                    }}
                  >
                    {/* Card Top Row: Badges & Due Date */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px", marginBottom: "10px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "700",
                            backgroundColor: isTest ? "#fee2e2" : "#eff6ff",
                            color: isTest ? "#b91c1c" : "#1d4ed8",
                            border: `1px solid ${isTest ? "#fca5a5" : "#bfdbfe"}`,
                            padding: "2px 8px",
                            borderRadius: "6px"
                          }}
                        >
                          {isTest ? "Timed Test" : "Homework"}
                        </span>

                        {isTest && testTiming && (
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: "700",
                              backgroundColor: testTiming.bg,
                              color: testTiming.color,
                              border: `1px solid ${testTiming.border}`,
                              padding: "2px 8px",
                              borderRadius: "6px"
                            }}
                          >
                            {testTiming.label}
                          </span>
                        )}

                        {set.classroomName && (
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: "600",
                              backgroundColor: "#f0fdf4",
                              color: "#166534",
                              border: "1px solid #bbf7d0",
                              padding: "2px 8px",
                              borderRadius: "6px"
                            }}
                          >
                            {set.classroomName}
                          </span>
                        )}

                        {tutor && (
                          <span style={{ fontSize: "12px", color: "#64748b", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            <span
                              style={{
                                width: "18px",
                                height: "18px",
                                borderRadius: "50%",
                                backgroundColor: "#eff6ff",
                                color: "#1d4ed8",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "9px",
                                fontWeight: "700"
                              }}
                            >
                              {getInitials(tutor.name)}
                            </span>
                            {tutor.name}
                          </span>
                        )}
                      </div>

                      {(set.dueTimestamp || set.dueDate) && (
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: set.isPastDeadline ? "700" : "500",
                            color: set.isPastDeadline ? "#dc2626" : "var(--muted)"
                          }}
                        >
                          {set.isPastDeadline ? "Overdue: " : "Due: "}
                          {new Date(set.dueTimestamp || set.dueDate).toLocaleString("en-IN", {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </div>
                      )}
                    </div>

                    {/* Test Duration & Schedule Window Banner */}
                    {isTest && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flexWrap: "wrap",
                          fontSize: "12px",
                          color: "#475569",
                          margin: "6px 0 12px 0",
                          backgroundColor: "#f8fafc",
                          padding: "6px 12px",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0"
                        }}
                      >
                        <span>⏱️ <strong>Duration:</strong> {set.durationMinutes || 60} mins</span>
                        {set.startTime && (
                          <span>
                            • <strong>Start:</strong>{" "}
                            {new Date(set.startTime).toLocaleString("en-IN", {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </span>
                        )}
                        {(set.dueTimestamp || set.dueDate) && (
                          <span>
                            • <strong>End:</strong>{" "}
                            {new Date(set.dueTimestamp || set.dueDate).toLocaleString("en-IN", {
                              month: "short",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Card Body: Title & Instructions */}
                    <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", margin: "0 0 6px 0" }}>
                      {set.title}
                    </h3>
                    {set.description && (
                      <p style={{ fontSize: "13px", color: "var(--muted)", margin: "0 0 12px 0" }}>
                        {set.description}
                      </p>
                    )}
                    {set.instructions && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#334155",
                          backgroundColor: "#f8fafc",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          borderLeft: "3px solid #3b82f6",
                          marginBottom: "14px"
                        }}
                      >
                        <strong>Instructions:</strong> {set.instructions}
                      </div>
                    )}

                    {/* ── Tutor Formative Feedback CTA Banner on Assignment Row ── */}
                    {hasFeedback && (
                      <div
                        onClick={() => setSelectedFeedbackModal({ set, feedback, tutor })}
                        style={{
                          backgroundColor: "#f5f3ff",
                          border: "1px solid #ddd6fe",
                          borderRadius: "10px",
                          padding: "10px 16px",
                          marginBottom: "14px",
                          boxShadow: "0 1px 2px rgba(109, 40, 217, 0.05)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12px",
                          flexWrap: "wrap",
                          cursor: "pointer",
                          transition: "all 0.15s ease"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0, flex: 1 }}>
                          <span style={{ fontSize: "16px" }}>💬</span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <span style={{ fontSize: "12px", fontWeight: "700", color: "#6d28d9" }}>
                                Teacher Feedback & Remarks
                              </span>
                              {feedback.gradedAt && (
                                <span style={{ fontSize: "11px", color: "#8b5cf6" }}>
                                  • {new Date(feedback.gradedAt).toLocaleDateString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                fontSize: "13px",
                                color: "#4c1d95",
                                fontWeight: "500",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "540px",
                                marginTop: "2px"
                              }}
                            >
                              "{feedback.notes || "Performance diagnostics reviewed."}"
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFeedbackModal({ set, feedback, tutor });
                          }}
                          style={{
                            padding: "6px 14px",
                            fontSize: "12px",
                            fontWeight: "600",
                            backgroundColor: "#6d28d9",
                            color: "#ffffff",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            flexShrink: 0
                          }}
                        >
                          View Detailed Feedback →
                        </button>
                      </div>
                    )}

                    {/* Card Footer: Progress / Score + Action Buttons */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "12px",
                        borderTop: "1px solid #f1f5f9",
                        paddingTop: "12px",
                        marginTop: "8px"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                        {hasFeedback && (
                          <button
                            type="button"
                            onClick={() => setSelectedFeedbackModal({ set, feedback, tutor })}
                            style={{
                              fontSize: "11px",
                              fontWeight: "700",
                              color: "#6d28d9",
                              backgroundColor: "#f5f3ff",
                              border: "1px solid #ddd6fe",
                              padding: "3px 10px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px"
                            }}
                          >
                            💬 View Feedback
                          </button>
                        )}
                        {isTest ? (
                          set.isGraded && set.totalMarksAwarded !== null ? (
                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#059669",
                                backgroundColor: "#ecfdf5",
                                padding: "3px 10px",
                                borderRadius: "6px",
                                border: "1px solid #a7f3d0"
                              }}
                            >
                              ✓ Graded: {set.totalMarksAwarded} / {set.totalMarks} Marks ({Math.round((set.totalMarksAwarded / (set.totalMarks || 1)) * 100)}%)
                            </span>
                          ) : set.isLocked ? (
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#4338ca",
                                backgroundColor: "#ede9fe",
                                padding: "3px 10px",
                                borderRadius: "6px"
                              }}
                            >
                              Submitted • Awaiting Teacher Grade Release
                            </span>
                          ) : (
                            <span style={{ fontSize: "12px", color: "var(--muted)" }}>
                              {set.questionCount} Problems • {set.totalMarks} Total Marks
                            </span>
                          )
                        ) : (set.isAssignmentSubmitted || set.isHomeworkSubmitted) ? (
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: "700",
                              color: "#059669",
                              backgroundColor: "#ecfdf5",
                              border: "1px solid #a7f3d0",
                              padding: "3px 10px",
                              borderRadius: "6px"
                            }}
                          >
                            ✓ Homework Submitted
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              color: set.completedCount > 0 ? "#2563eb" : "#475569"
                            }}
                          >
                            {set.completedCount > 0 ? `${set.completedCount} of ${set.questionCount} Solved (In Progress)` : `${set.questionCount} Problems Assigned`}
                          </span>
                        )}

                        <button
                          type="button"
                          onClick={() => toggleSetExpand(set.id)}
                          style={{
                            background: "none",
                            border: "none",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#2563eb",
                            cursor: "pointer",
                            padding: "2px 6px"
                          }}
                        >
                          {isExpanded ? "Hide Problems ▲" : `View Problems (${set.questionCount}) ▼`}
                        </button>
                      </div>

                      {/* Primary Action Button */}
                      <div>
                        {isTest ? (
                          set.isGraded ? (
                            <button
                              type="button"
                              className="primary-button"
                              onClick={() => toggleSetExpand(set.id)}
                              style={{ fontSize: "13px", padding: "7px 16px", backgroundColor: "#059669" }}
                            >
                              Review Marks & Feedback →
                            </button>
                          ) : set.isLocked ? (
                            <button
                              type="button"
                              className="secondary-button"
                              onClick={() => toggleSetExpand(set.id)}
                              style={{ fontSize: "13px", padding: "7px 16px" }}
                            >
                              View Submission →
                            </button>
                          ) : testTiming && !testTiming.canStart && testTiming.status === "upcoming" ? (
                            <button
                              type="button"
                              disabled
                              style={{
                                fontSize: "13px",
                                padding: "7px 16px",
                                backgroundColor: "#f1f5f9",
                                color: "#64748b",
                                border: "1.5px solid #cbd5e1",
                                borderRadius: "6px",
                                cursor: "not-allowed",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                fontWeight: "600"
                              }}
                              title={`This exam opens at ${testTiming.startTimeFormatted}`}
                            >
                              <span>🔒</span> Starts at {testTiming.startTimeFormatted}
                            </button>
                          ) : set.isPastDeadline ? (
                            <span style={{ fontSize: "13px", color: "#dc2626", fontWeight: "600" }}>
                              Deadline Expired (Locked)
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="primary-button"
                              onClick={() => {
                                if (set.firstQuestionId) {
                                  navigate(`/practice/custom/${set.firstQuestionId}?setId=${set.id}`, {
                                    state: { backTo: "/my-tutor" }
                                  });
                                }
                              }}
                              style={{ fontSize: "13px", padding: "7px 16px", backgroundColor: "#2563eb" }}
                            >
                              {set.completedCount > 0 ? "Resume Test →" : "Start Timed Test →"}
                            </button>
                          )
                        ) : (
                          <button
                            type="button"
                            className="primary-button"
                            onClick={() => {
                              if (set.firstQuestionId) {
                                navigate(`/practice/custom/${set.firstQuestionId}?setId=${set.id}`, {
                                  state: { backTo: "/my-tutor" }
                                });
                              }
                            }}
                            style={{
                              fontSize: "13px",
                              padding: "7px 16px",
                              backgroundColor: (set.isAssignmentSubmitted || set.isHomeworkSubmitted) ? "#059669" : "#2563eb"
                            }}
                          >
                            {(set.isAssignmentSubmitted || set.isHomeworkSubmitted) ? "Review Homework →" : (set.completedCount > 0 ? "Resume Homework →" : "Solve Homework →")}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Collapsible Problems Drawer */}
                    {isExpanded && Array.isArray(set.questions) && set.questions.length > 0 && (
                      <div
                        style={{
                          marginTop: "16px",
                          borderTop: "1px dashed var(--border)",
                          paddingTop: "14px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px"
                        }}
                      >
                        {set.questions.map((q, qIdx) => (
                          <div
                            key={q.id || q._id}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              backgroundColor: "#f8fafc",
                              borderRadius: "8px",
                              padding: "10px 14px",
                              border: "1px solid #e2e8f0",
                              flexWrap: "wrap",
                              gap: "8px"
                            }}
                          >
                            <div>
                              <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b" }}>
                                Problem {qIdx + 1}: {q.title}
                                {isTest && (
                                  <span style={{ color: "#4f46e5", marginLeft: "6px" }}>
                                    ({q.marks || 10} M)
                                  </span>
                                )}
                              </div>
                              {q.latex && (
                                <div style={{ fontSize: "12px", marginTop: "2px", overflowX: "auto" }}>
                                  <BlockMath math={formatLatexForDisplay(q.latex)} />
                                </div>
                              )}
                              {q.tutorFeedback?.notes && (
                                <div style={{ fontSize: "12px", color: "#6d28d9", marginTop: "4px" }}>
                                  <strong>Teacher Remark:</strong> {q.tutorFeedback.notes}
                                </div>
                              )}
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              {isTest && q.gradesPublished && q.marksAwarded !== null && (
                                <span style={{ fontSize: "12px", fontWeight: "700", color: "#059669", backgroundColor: "#ecfdf5", padding: "2px 8px", borderRadius: "4px" }}>
                                  {q.marksAwarded} / {q.marks || 10} M
                                </span>
                              )}

                              {q.latestAttemptId ? (
                                <button
                                  type="button"
                                  className="secondary-button"
                                  onClick={() =>
                                    navigate(`/attempts/${q.latestAttemptId}`, {
                                      state: { backTo: "/my-tutor" }
                                    })
                                  }
                                  style={{ fontSize: "11px", padding: "4px 10px" }}
                                >
                                  View Attempt →
                                </button>
                              ) : !set.isLocked ? (
                                <button
                                  type="button"
                                  className="primary-button"
                                  onClick={() =>
                                    navigate(`/practice/custom/${q.id || q._id}?setId=${set.id}`, {
                                      state: { backTo: "/my-tutor" }
                                    })
                                  }
                                  style={{ fontSize: "11px", padding: "4px 10px" }}
                                >
                                  Solve →
                                </button>
                              ) : (
                                <span style={{ fontSize: "11px", color: "var(--muted)" }}>Not attempted</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TAB 2: LIVE SESSIONS & FULL CALENDAR
          ══════════════════════════════════════════════════════════════ */}
      {activeTab === "calendar" && (
        <div>
          {/* Calendar Header with Tutor Filter */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
              marginBottom: "16px"
            }}
          >
            <div style={{ fontSize: "14px", color: "var(--muted)" }}>
              Select a date on the calendar to view scheduled 1:1 sessions or group classes.
            </div>

            {tutors.length > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "12px", color: "var(--muted)" }}>Filter Tutor:</span>
                <select
                  value={selectedTutorFilter}
                  onChange={(e) => setSelectedTutorFilter(e.target.value)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "6px",
                    fontSize: "13px",
                    border: "1px solid var(--border)",
                    backgroundColor: "#ffffff"
                  }}
                >
                  <option value="all">All Tutors</option>
                  {tutors.map((tut) => (
                    <option key={tut.uid || tut.id} value={tut.uid || tut.id}>
                      {tut.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Full Calendar */}
          <div
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid var(--border)",
              borderRadius: "16px",
              padding: "20px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.02)"
            }}
          >
            <ClassCalendar
              sessions={filteredSessions}
              isTutor={false}
              loading={loading}
              onSelectSession={(sess) => setSelectedSessionModal(sess)}
            />
          </div>
        </div>
      )}

      {/* ── Session Detail Modal ── */}
      {selectedSessionModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px"
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              padding: "24px",
              maxWidth: "480px",
              width: "100%",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  backgroundColor: selectedSessionModal.sessionType === "one_on_one" ? "#fdf2f8" : "#eff6ff",
                  color: selectedSessionModal.sessionType === "one_on_one" ? "#be185d" : "#1d4ed8",
                  padding: "2px 8px",
                  borderRadius: "4px"
                }}
              >
                {selectedSessionModal.sessionType === "one_on_one" ? "1:1 Session" : "Group Class"}
              </span>
              <button
                type="button"
                onClick={() => setSelectedSessionModal(null)}
                style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "var(--muted)" }}
              >
                ✕
              </button>
            </div>

            <h2 style={{ fontSize: "18px", fontWeight: "700", margin: "0 0 8px 0", color: "#0f172a" }}>
              {selectedSessionModal.title}
            </h2>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#334155", marginBottom: "20px" }}>
              <div>
                <strong>Class / Batch:</strong> {selectedSessionModal.classroomName || "General"}
              </div>
              <div>
                <strong>Date & Time:</strong>{" "}
                {new Date(selectedSessionModal.startTime).toLocaleString("en-IN", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit"
                })}{" "}
                ({selectedSessionModal.durationMinutes || 60} mins)
              </div>
              {selectedSessionModal.notes && (
                <div>
                  <strong>Agenda / Topics:</strong> {selectedSessionModal.notes}
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setSelectedSessionModal(null)}
              >
                Close
              </button>
              {selectedSessionModal.meetUrl && (
                <a
                  href={selectedSessionModal.meetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="primary-button"
                  style={{ textDecoration: "none", backgroundColor: "#059669", fontSize: "13px", padding: "8px 16px" }}
                >
                  Join Online Class →
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Tutor Detailed Feedback Modal Overlay ── */}
      {selectedFeedbackModal && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setSelectedFeedbackModal(null)}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px"
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              padding: "24px 28px",
              maxWidth: "580px",
              width: "100%",
              maxHeight: "85vh",
              overflowY: "auto",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              border: "1px solid #e2e8f0"
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "50%",
                    backgroundColor: "#f5f3ff",
                    color: "#7c3aed",
                    border: "1px solid #ddd6fe",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: "700"
                  }}
                >
                  {getInitials(selectedFeedbackModal.tutor?.name || "Tutor")}
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>
                      {selectedFeedbackModal.tutor?.name || "Your Tutor"}
                    </span>
                    <span style={{ fontSize: "10px", fontWeight: "700", backgroundColor: "#f5f3ff", color: "#6d28d9", padding: "1px 6px", borderRadius: "4px" }}>
                      Active Tutor
                    </span>
                  </div>
                  <div style={{ fontSize: "12px", color: "var(--muted)" }}>
                    Formative Feedback & Study Guidance
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedFeedbackModal(null)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "18px",
                  cursor: "pointer",
                  color: "#64748b",
                  padding: "4px 8px",
                  borderRadius: "6px"
                }}
              >
                ✕
              </button>
            </div>

            {/* Assignment Context Banner */}
            <div
              style={{
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "10px 14px",
                marginBottom: "16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px"
              }}
            >
              <div>
                <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#64748b" }}>
                  Assignment
                </div>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#0f172a" }}>
                  {selectedFeedbackModal.set?.title}
                </div>
              </div>
              {selectedFeedbackModal.feedback?.gradedAt && (
                <div style={{ fontSize: "11px", color: "#64748b" }}>
                  Received: {new Date(selectedFeedbackModal.feedback.gradedAt).toLocaleDateString([], {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                  })}
                </div>
              )}
            </div>

            {/* Teacher's Personal Remarks */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", color: "#475569", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                Teacher Remarks
              </div>
              <div
                style={{
                  backgroundColor: "#faf5ff",
                  borderLeft: "4px solid #8b5cf6",
                  borderTop: "1px solid #f3e8ff",
                  borderRight: "1px solid #f3e8ff",
                  borderBottom: "1px solid #f3e8ff",
                  borderRadius: "8px",
                  padding: "14px 16px",
                  fontSize: "14px",
                  color: "#3b0764",
                  lineHeight: "1.6",
                  fontWeight: "500",
                  fontStyle: "italic"
                }}
              >
                "{selectedFeedbackModal.feedback?.notes || "Performance diagnostics reviewed. Solid conceptual rigor demonstrated!"}"
              </div>
            </div>

            {/* Identified Strengths */}
            {Array.isArray(selectedFeedbackModal.feedback?.strengths) && selectedFeedbackModal.feedback.strengths.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#065f46", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                  <span>🌟</span> Identified Strengths
                </div>
                <div style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "12px 16px" }}>
                  <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "13px", color: "#14532d", display: "flex", flexDirection: "column", gap: "4px" }}>
                    {selectedFeedbackModal.feedback.strengths.map((st, i) => (
                      <li key={i}>{st}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Recommended Focus Points */}
            {Array.isArray(selectedFeedbackModal.feedback?.focusPoints) && selectedFeedbackModal.feedback.focusPoints.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#92400e", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                  <span>🎯</span> Recommended Practice Focus
                </div>
                <div style={{ backgroundColor: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", padding: "12px 16px" }}>
                  <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "13px", color: "#78350f", display: "flex", flexDirection: "column", gap: "4px" }}>
                    {selectedFeedbackModal.feedback.focusPoints.map((fp, i) => (
                      <li key={i}>{fp}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Problem-by-problem notes */}
            {Array.isArray(selectedFeedbackModal.set?.questions) &&
              selectedFeedbackModal.set.questions.some((q) => q.tutorFeedback?.notes) && (
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "6px" }}>
                    Problem-by-Problem Notes
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {selectedFeedbackModal.set.questions.map((q, idx) => {
                      if (!q.tutorFeedback?.notes) return null;
                      return (
                        <div
                          key={q.id || q._id || idx}
                          style={{
                            padding: "8px 12px",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            fontSize: "12px"
                          }}
                        >
                          <div style={{ fontWeight: "600", color: "#1e293b", marginBottom: "2px" }}>
                            Problem {idx + 1}: {q.title || `Question ${idx + 1}`}
                          </div>
                          <div style={{ color: "#475569" }}>{q.tutorFeedback.notes}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {/* Modal Footer with Close Window CTA */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px", paddingTop: "14px", borderTop: "1px solid #f1f5f9" }}>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setSelectedFeedbackModal(null)}
                style={{ padding: "8px 20px", fontSize: "13px", fontWeight: "600" }}
              >
                Close Window
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

